// move to @epic-node-services/common
import {AffiliateReport as BaseAffiliateReport} from '@epic-node-services/common';
import {ChannelStatus} from './';
import {Talon} from './Talon';

export enum ApplicationState {
    NONE = 'NONE',
    REJECTED_APPLICANT = 'REJECTED_APPLICANT',
    REGISTERED_AFFILIATE = 'REGISTERED_AFFILIATE'
}

export enum QualifiedGenreType {
    SAC = 'SAC', // BETA
    CREATIVE = 'CREATIVE' // CREATIVE DEVELOPER
}

export interface QualifiedGenre {
    type: QualifiedGenreType;
    whitelisted: boolean;
}

// Derives from QualifiedGenreType (we transform it)
export enum WhitelistType {
    SAC = 'isBeta',
    CREATIVE = 'isCreative'
}

export interface Whitelist {
    isBeta: boolean;
    isCreative: boolean;
}

export enum StatusReason {
    // DISABLED_UNDER_18_NEEDS_GUARDIAN = 'DISABLED_UNDER_18_NEEDS_GUARDIAN',
    DELETED_EXIT_SAC_PROGRAM = 'DELETED_EXIT_SAC_PROGRAM',
    DELETED_PAYOUT_SETUP_OVERDUE = 'DELETED_PAYOUT_SETUP_OVERDUE',
    DELETED_SANCTIONED_TAX_COUNTRY = 'DELETED_SANCTIONED_TAX_COUNTRY',
    DELETED_VIOLATED_OAUTH_REQUIREMENTS = 'DELETED_VIOLATED_OAUTH_REQUIREMENTS',
    DISABLED_BAD_ACTOR = 'DISABLED_BAD_ACTOR',
    DISABLED_BAD_SLUG = 'DISABLED_BAD_SLUG',
    DISABLED_DECLINED_EULA = 'DISABLED_DECLINED_EULA',
    DISABLED_DID_NOT_MIGRATE = 'DISABLED_DID_NOT_MIGRATE',
    DISABLED_FAILED_FRAUD_CHECK = 'DISABLED_FAILED_FRAUD_CHECK',
    DISABLED_FAILED_TALON_CHECK = 'DISABLED_FAILED_TALON_CHECK',
    DISABLED_HYPERWALLET_FRAUD_RISK = 'DISABLED_HYPERWALLET_FRAUD_RISK',
    DISABLED_NEEDS_SOCIAL_UPDATE = 'DISABLED_NEEDS_SOCIAL_UPDATE',
    DISABLED_OTHER = 'DISABLED_OTHER',
    DISABLED_OTHER_RUSSIA = 'DISABLED_OTHER_RUSSIA',
    DISABLED_RESTRICTED_REGION = 'DISABLED_RESTRICTED_REGION',
    DISABLED_SANCTIONED_TAX_COUNTRY = 'DISABLED_SANCTIONED_TAX_COUNTRY',
    DISABLED_UNDER_13 = 'DISABLED_UNDER_13',
    DISABLED_VIOLATED_OAUTH_REQUIREMENTS = 'DISABLED_VIOLATED_OAUTH_REQUIREMENTS',
    DISABLED_VIOLATED_TOS = 'DISABLED_VIOLATED_TOS',
    REJECTED_FAILED_FRAUD_CHECK = 'REJECTED_FAILED_FRAUD_CHECK'
}

export enum Relationship {
    FATHER = 'FATHER',
    MOTHER = 'MOTHER',
    OTHER = 'OTHER'
}

export interface GuardianInfo {
    firstName: string;
    lastName: string;
    relationship: Relationship | string;
}

export enum SocialPlatform {
    // WEBSITE_CHANNEL = 'WEBSITE_CHANNEL',
    // PODCAST_CHANNEL = 'PODCAST_CHANNEL',
    // MIXER = 'MIXER',
    // TIKTOK = 'TIKTOK',
    YOUTUBE_CHANNEL = 'YOUTUBE_CHANNEL',
    TWITCH_CHANNEL = 'TWITCH_CHANNEL',
    FACEBOOK = 'FACEBOOK',
    INSTAGRAM = 'INSTAGRAM',
    TWITTER = 'TWITTER',
    VK = 'VK'
}

export interface OAuthDetails {
    lastValidatedAt: string;
    scope: string;
    expireIn: number;
    tokenType: string;
    accessToken: string;
    refreshToken: string;
    valid: boolean;
}

export enum ChannelViolation {
    X_OAUTH_TOKEN = 'X_OAUTH_TOKEN',
    X_FOLLOWER_COUNT = 'X_FOLLOWER_COUNT',
    X_OWNED = 'X_OWNED',
    X_GOOD_STANDING = 'X_GOOD_STANDING',
    X_PUBLIC = 'X_PUBLIC',
    X_CONNECTED = 'X_CONNECTED'
}

export interface SocialChannel {
    id?: string;
    platform: SocialPlatform;
    link: string;
    followers: number;
    mainChannel: boolean;
    channelStatus: ChannelStatus;
    oauthDetails: Partial<OAuthDetails>;
    violations?: ChannelViolation[] | string[];
    impossibleCheck?: boolean;
    channelId?: string;
}

export interface UpdateSocialChannel {
    id?: string;
    followers: number;
    channelStatus: ChannelStatus;
    oauthDetails: Partial<OAuthDetails>;
}

export interface UpdateSocialChannels {
    mainChannelId?: string;
    socialChannels?: UpdateSocialChannel[];
}

export enum Status {
    INIT = 'INIT',
    PENDING = 'PENDING',
    REJECTED = 'REJECTED',
    ACTIVE = 'ACTIVE',
    DISABLED = 'DISABLED',
    DELETED = 'DELETED'
}

export interface OAuthViolation {
    fixDueDate: string;
}

export interface PayoutSetupInfo {
    completed: boolean;
    dueDate: string; // 2021-11-27T21:42:12.840Z
    taxProfileStatus: 'NONE' | 'PENDING' | 'COMPLETED';
    walletStatus: 'NONE' | 'PENDING' | 'COMPLETED';
    paymentConsent: boolean;
}

export interface SystemEulaServiceMapping {
    key: 'sac_agreement';
    version: number;
}

export interface System {
    configId: number; // 1, 2, 3
    friendlyVersionName: string; // 2.0
    releasedAt: string; // 20210126-01-01T00:00:00.000Z
    migrateBy: string; // 20210426-01-01T00:00:00.000Z
    eulaServiceMapping: SystemEulaServiceMapping;
}

export interface DecisionDetails {
    date: string; // 2021-01-27T01:35:40.955Z
    fromVersion: string;
    toVersion: string;
    decision: 'ACCEPT' | 'DECLINE';
    voluntary: boolean;
}

export interface CurrentVersion {
    configId: number; // Aboluste Whole Numbers (version)
    friendlyVersionName: string;
    obsolete: boolean;
    decisionDetails?: DecisionDetails;
}

export interface AgreementInfo {
    system: System;
    currentVersion: CurrentVersion;
}

export interface EarningsInfo {
    earningsWindowStart: string; // ISO
    earningsDeadline: string; // ISO
    lastEarningsHitMonth: string;
}

export interface Affiliate {
    id: string; // same as account id
    applicationState: ApplicationState;
    agreementInfo: AgreementInfo;
    v2DecisionDate?: string;
    status: Status;
    statusReason: StatusReason;
    qualifiedGenres?: QualifiedGenre[];
    slug: string;
    displayName: string;
    city: string;
    state: string;
    country: string;
    language: string;
    ageConfirmed: boolean;
    guardianInfo: GuardianInfo;
    socialChannels: SocialChannel[];
    payoutSetupInfo: PayoutSetupInfo;
    createdAt: string;
    lastModifiedAt: string;
    extendedMigrationWindow?: boolean;
    earliestReapplicationDate?: string;
    statusEffectiveDate: string;
    oauthViolation?: OAuthViolation;
    canReinstate?: boolean;
    // custom frontend attribute
    whitelist?: Partial<Whitelist>;
    canEditSettings?: boolean;
    earningsInfo?: EarningsInfo;
}

export interface UpdateAffiliate extends Affiliate {
    mainChannelId?: string;
}

export interface Eula {
    key: string;
    version: number;
    locale: string;
}

export interface PayoutInfo {
    currentEligibleEarningsAmount: number;
    lastPaymentAmount: number;
    lastPaymentDate: string;
    lifetimePaymentAmount: number;
}

export interface Earnings {
    payoutInfo: PayoutInfo;
    estimatedFinalPaymentAmount?: number;
}

export enum Alerts {
    GOOD_STANDING = 'GOOD_STANDING',
    MIGRATE_PROFILE = 'MIGRATE_PROFILE',
    CREATE_TAX_PROFILE = 'CREATE_TAX_PROFILE',
    FN_CREATIVE_CCU = 'FN_CREATIVE_CCU',
    INELIGIBLE_SOCIAL = 'INELIGIBLE_SOCIAL',
    PENDING_DELETION = 'PENDING_DELETION',
    CONSENT_RELEASE_FORM_V2 = 'CONSENT_RELEASE_FORM_V2',
    DECLINED_EULA_CASH = 'DECLINED_EULA_CASH',
    DECLINED_EULA_VBUCKS = 'DECLINED_EULA_VBUCKS',
    SELECT_PRIMARY_CHANNEL = 'SELECT_PRIMARY_CHANNEL',
    RESET_EARNINGS = 'RESET_EARNINGS',
    RESET_EARNINGS_WARNING = 'RESET_EARNINGS_WARNING'
}

export interface SlugAvailabilityReservation {
    slug: string;
    until: string; // ISO timestamp
}

export interface SlugAvailabilityUsage {
    statusReasonEnum: string;
    affiliateId: string;
    slug: string;
    status: string;
}

export interface SlugAvailabilityReservationDetail {
    usages: SlugAvailabilityUsage[];
    reservations: SlugAvailabilityReservation[];
}

export interface SlugAvailabilityReason {
    valid: boolean;
    toxic: boolean;
    reserved: boolean;
    reservationDetail: SlugAvailabilityReservationDetail;
}

export interface SlugAvailability {
    slug: string;
    available: boolean;
    taken?: boolean;
    reason?: SlugAvailabilityReason;
}

export interface FinalPayout {
    amount: number;
    vBucks: number;
}

export interface CodeAvailability {
    slug: string;
    available: boolean;
    taken: boolean;
}

export interface AgreementConfig {
    latest: System;
    configurationsById: Record<string, System>;
    rvn: number;
}

export interface AcceptAgreementConfig {
    query: {
        agreementConfigId: number;
    };
    body: {
        decisionRequest: Talon & {
            slug: string;
        };
    };
}

export interface DeclineAgreementConfig {
    query: {
        agreementConfigId: number;
        forfeitEarnings: boolean;
    };
    body: {
        decisionRequest: Talon & {
            slug: string;
        };
    };
}

export type AffiliateReport = Omit<Partial<BaseAffiliateReport>, 'status'> & {
    status?: string;
};
